import React, { useMemo } from "react";
import { ButtonSize } from "../../../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../../../classes/enums/IconsEnum";
import { ActNatures } from "../../../../../classes/enums/SurgicalActsEnum";
import { vertebraUtils } from "../../../../../classes/utils/AnnotationsUtils";
import Button from "../../../../../components/button/Button";
import IconButton from "../../../../../components/iconButton/IconButton";
import WidgetBox from "../../../../../components/widgetBox/WidgetBox";
import useTranslation from "../../../../../hooks/i18n/useTranslation";
import "../History.css";

// Section header component
const SectionHeader = ({ title, onEdit, disabled }) => {
  const { t } = useTranslation();
  return (
    <div className="rod-history-section-header">
      <span className="App-text-medium">{title}</span>
      {onEdit && (
        <IconButton
          icon={IconsEnum.EDIT}
          size={ButtonSize.SMALL}
          isCircle={true}
          noBgColor
          onClick={onEdit}
          disabled={disabled}
          title={t("208")}
        />
      )}
    </div>
  );
};

// Rod entry component
const RodEntry = ({
  rodPair,
  isSelected,
  onToggleSelect,
  onDownload,
  disabled,
  showCheckbox = true,
  showDownload = false,
}) => {
  const formatVertebraRange = rodVertebraeSubset => {
    if (rodVertebraeSubset && rodVertebraeSubset.length > 1) {
      const first = vertebraUtils.getVertebraLabel(rodVertebraeSubset[0]);
      const last = vertebraUtils.getVertebraLabel(
        rodVertebraeSubset[rodVertebraeSubset.length - 1]
      );
      return `${first}→${last}`;
    }
    if (rodVertebraeSubset && rodVertebraeSubset.length === 1) {
      return vertebraUtils.getVertebraLabel(rodVertebraeSubset[0]);
    }
    return "N/A";
  };

  const getSideLabel = () => {
    if (rodPair.left && rodPair.right) {
      return "L/R";
    }
    if (rodPair.left) {
      return "L";
    }
    if (rodPair.right) {
      return "R";
    }
    return "N/A";
  };

  const getDiameter = () => {
    const refRod = rodPair.left || rodPair.right;
    return refRod?.rodDiameter ? `⌀${refRod.rodDiameter}mm` : "N/A";
  };

  const getLength = () => {
    const refRod = rodPair.left || rodPair.right;
    return refRod?.rodCalculatedLength || "N/A";
  };

  const getRange = () => {
    const refRod = rodPair.left || rodPair.right;
    return formatVertebraRange(refRod?.rodVertebraeSubset);
  };

  return (
    <div className={`history-item ${isSelected ? "selected" : ""}`}>
      <div className="history-row rod-entry-row">
        {showCheckbox && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            disabled={disabled}
            className="history-item-checkbox"
          />
        )}
        <div className="App-text-medium rod-entry-range">{getRange()}</div>
        <div className="App-text-medium rod-entry-side">{getSideLabel()}</div>
        <div className="App-text-medium rod-entry-diameter">
          {getDiameter()}
        </div>
        <div className="App-text-medium rod-entry-length">{getLength()}</div>
        {showDownload && (
          <div className="rod-entry-action">
            <IconButton
              icon={IconsEnum.EXPORT}
              size={ButtonSize.SMALL}
              isCircle={true}
              onClick={onDownload}
              disabled={disabled}
              tooltipContent="Download"
              noBgColor
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Screw entry component
const ScrewEntry = ({ label, count }) => (
  <div className="screw-entry">
    <span className="App-text-medium">{label}</span>
    <span className="App-text-medium screw-count">×{count}</span>
  </div>
);

const RodHistory = ({
  onDeleteSurgicalAct,
  onBulkDeleteSurgicalActs,
  onEditSurgicalAct,
  onEditAllRods,
  currentSurgicalActs,
  comparedSurgicalActs = [],
  compareMode = false,
  currentPlanLabel = "Plan 1",
  comparedPlanLabel = "Plan 2",
}) => {
  const [isLoading, setLoading] = React.useState(false);
  const [selectedPairs, setSelectedPairs] = React.useState(new Set());

  const normalizeActs = actsSource => {
    if (!actsSource) {
      return [];
    }
    return Array.isArray(actsSource) ? actsSource : Object.values(actsSource);
  };

  const surgicalActsArray = normalizeActs(currentSurgicalActs);
  const comparedActsArray = normalizeActs(comparedSurgicalActs);

  // Filter only rod acts
  const rodActs = surgicalActsArray.filter(
    act => act.actNature === ActNatures.Rod
  );
  const comparedRodActs = comparedActsArray.filter(
    act => act.actNature === ActNatures.Rod
  );

  const buildRodData = acts => {
    const rodPairsMap = new Map();

    acts.forEach(act => {
      const pairKey = act.rodPairId || (act.rodVertebraeSubset || []).join("-");

      if (!rodPairsMap.has(pairKey)) {
        rodPairsMap.set(pairKey, {
          pairId: pairKey,
          left: null,
          right: null,
          acts: [],
        });
      }

      const pair = rodPairsMap.get(pairKey);
      pair.acts.push(act);

      if (act.side === "left") {
        pair.left = act;
      } else if (act.side === "right") {
        pair.right = act;
      }
    });

    const rodPairs = Array.from(rodPairsMap.values());
    const entries = [];

    rodPairs.forEach(pair => {
      const leftRange = pair.left?.rodVertebraeSubset?.join("-") || "";
      const rightRange = pair.right?.rodVertebraeSubset?.join("-") || "";

      if (pair.left && pair.right && leftRange !== rightRange) {
        entries.push({
          pairId: `${pair.pairId}-left`,
          left: pair.left,
          right: null,
          acts: [pair.left],
        });
        entries.push({
          pairId: `${pair.pairId}-right`,
          left: null,
          right: pair.right,
          acts: [pair.right],
        });
      } else {
        entries.push(pair);
      }
    });

    const screwSummary = (() => {
      const summaryMap = new Map();

      acts.forEach(act => {
        if (act.screws) {
          if (act.screws.L) {
            Object.values(act.screws.L).forEach(screw => {
              const label = `${screw.diameter} mm × ${screw.length} mm`;
              summaryMap.set(label, (summaryMap.get(label) || 0) + 1);
            });
          }
          if (act.screws.R) {
            Object.values(act.screws.R).forEach(screw => {
              const label = `${screw.diameter} mm × ${screw.length} mm`;
              summaryMap.set(label, (summaryMap.get(label) || 0) + 1);
            });
          }
        } else if (act.screwDiameter && act.screwLength) {
          const label = `${act.screwDiameter} mm × ${act.screwLength} mm`;
          const count = act.screwCount || 1;
          summaryMap.set(label, (summaryMap.get(label) || 0) + count);
        }
      });

      return Array.from(summaryMap.entries()).map(([label, count]) => ({
        label,
        count,
      }));
    })();

    return {
      entries,
      screwSummary,
    };
  };

  const { entries: rodEntries, screwSummary } = useMemo(
    () => buildRodData(rodActs),
    [rodActs]
  );

  const { entries: comparedRodEntries, screwSummary: comparedScrewSummary } =
    useMemo(() => buildRodData(comparedRodActs), [comparedRodActs]);

  const handleToggleSelect = pairId => {
    const newSelected = new Set(selectedPairs);
    if (newSelected.has(pairId)) {
      newSelected.delete(pairId);
    } else {
      newSelected.add(pairId);
    }
    setSelectedPairs(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPairs.size === rodEntries.length) {
      setSelectedPairs(new Set());
    } else {
      const allPairIds = new Set(rodEntries.map(entry => entry.pairId));
      setSelectedPairs(allPairIds);
    }
  };

  const handleBulkDelete = async () => {
    try {
      if (isLoading || selectedPairs.size === 0) {
        return;
      }

      setLoading(true);

      // Collect all acts from selected pairs
      const selectedActs = rodEntries
        .filter(entry => selectedPairs.has(entry.pairId))
        .flatMap(entry => entry.acts);

      if (onBulkDeleteSurgicalActs && selectedActs.length > 0) {
        await onBulkDeleteSurgicalActs(selectedActs);
        setSelectedPairs(new Set());
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (onEditAllRods) {
      onEditAllRods(rodActs);
    }
  };

  const handleDownload = rodPair => {
    // TODO: Implement download functionality
  };

  if (rodActs.length === 0 && !compareMode) {
    return null;
  }

  const hasSelection = selectedPairs.size > 0;
  const isAllSelected = selectedPairs.size === rodEntries.length;

  const renderPlanSection = (label, entries, screws) => (
    <div className="history-compare-plan">
      <div className="history-compare-plan-title App-text-medium">{label}</div>

      {entries.length === 0 ? (
        <div className="history-empty App-text-small">No rods</div>
      ) : (
        <>
          <div className="rod-history-section">
            {entries.map(entry => (
              <RodEntry
                key={entry.pairId}
                rodPair={entry}
                isSelected={false}
                onToggleSelect={() => {}}
                onDownload={() => {}}
                disabled={true}
                showCheckbox={false}
                showDownload={false}
              />
            ))}
          </div>
          {screws.length > 0 && (
            <div className="rod-history-section screws-section">
              {screws.map((screw, index) => (
                <ScrewEntry
                  key={`${screw.label}-${index}`}
                  label={screw.label}
                  count={screw.count}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  if (compareMode) {
    if (rodEntries.length === 0 && comparedRodEntries.length === 0) {
      return null;
    }

    return (
      <WidgetBox
        title="Fusion Rods"
        isSmall={true}
        isInSideBar={true}
        isExpandable={true}
      >
        <div className="side-widget-history history-compare-wrapper">
          {renderPlanSection(currentPlanLabel, rodEntries, screwSummary)}
          <div className="history-compare-separator" />
          {renderPlanSection(
            comparedPlanLabel,
            comparedRodEntries,
            comparedScrewSummary
          )}
        </div>
      </WidgetBox>
    );
  }

  return (
    <WidgetBox
      title="Fusion Rods"
      isSmall={true}
      isInSideBar={true}
      isExpandable={true}
    >
      <div className="side-widget-history">
        {/* Selection controls */}
        <div className="history-selection-controls">
          <label className="select-all-checkbox">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              disabled={isLoading}
            />
            <span className="App-text-small">
              {isAllSelected ? "Deselect All" : "Select All"}
            </span>
          </label>
          {hasSelection && (
            <span className="App-text-small selection-count">
              {selectedPairs.size} selected
            </span>
          )}
        </div>

        {/* Rods Section */}
        <SectionHeader title="Rods" onEdit={handleEdit} disabled={isLoading} />
        <div className="rod-history-section">
          {rodEntries.map(entry => (
            <RodEntry
              key={entry.pairId}
              rodPair={entry}
              isSelected={selectedPairs.has(entry.pairId)}
              onToggleSelect={() => handleToggleSelect(entry.pairId)}
              onDownload={() => handleDownload(entry)}
              disabled={isLoading}
            />
          ))}
        </div>

        {/* Screws Section */}
        {screwSummary.length > 0 && (
          <>
            <SectionHeader title="Screws" disabled={isLoading} />
            <div className="rod-history-section screws-section">
              {screwSummary.map((screw, index) => (
                <ScrewEntry
                  key={`${screw.label}-${index}`}
                  label={screw.label}
                  count={screw.count}
                />
              ))}
            </div>
          </>
        )}

        {/* Bulk delete button */}
        {hasSelection && (
          <div className="bulk-delete-container">
            <Button
              value={`Delete (${selectedPairs.size})`}
              onClick={handleBulkDelete}
              disabled={isLoading}
              className="bulk-delete-button"
            />
          </div>
        )}
      </div>
    </WidgetBox>
  );
};

export default RodHistory;
